import { useAuthStore } from '@/modules/auth/store/auth';
import type { User } from '@/modules/auth/types';
import type { CommonErrorResponse } from '@/types/ErrorResponse';
import { storeToRefs } from 'pinia';

type SessionResponse = {
  data?: User;
};

type CompletePostAuthOptions = {
  accessToken?: string;
  user?: User | null;
  redirect?: string | null;
};

type PostAuthOnboardingRedirectState = {
  isCustomer?: boolean;
  isVerified?: boolean;
  hasPersonalDetails?: boolean;
  hasCompletedOnboarding?: boolean;
};

export function getPostAuthOnboardingRedirect(state: PostAuthOnboardingRedirectState): 'verify' | 'onboarding' | null {
  if (state.isCustomer && !state.isVerified) {
    return 'verify';
  }

  if (state.isCustomer && (!state.hasPersonalDetails || !state.hasCompletedOnboarding)) {
    return 'onboarding';
  }

  if (!state.isCustomer && !state.hasCompletedOnboarding) {
    return 'onboarding';
  }

  return null;
}

function getSafeRedirectTarget(redirect?: string | null): string | null {
  if (!redirect || !redirect.startsWith('/') || redirect.startsWith('//')) {
    return null;
  }

  return redirect;
}

export function usePostAuthRedirect() {
  const authStore = useAuthStore();
  const {
    user,
    isVerified,
    hasCompletedOnboarding,
    hasPersonalDetails,
    hasStripePayoutCompleted,
    isCustomer,
    isMerchant,
  } = storeToRefs(authStore);
  const router = useRouter();
  const route = useRoute();
  const { setToken } = useAuthState();
  const { data: authSession, getSession } = useAuth();
  const { fetchCarts, loading: fetchingCarts } = useCart();
  const { $posthog } = useNuxtApp();

  const {
    pending: verifyLoading,
    execute: execSendVerify,
    error: verifyError,
  } = useSubmit<{}, { data: CommonErrorResponse<any> }>();

  async function completePostAuth(options: CompletePostAuthOptions): Promise<void> {
    if (options.accessToken) {
      setToken(options.accessToken);
    }

    const session = (await getSession()) as SessionResponse | undefined;
    const sessionUser = options.user || session?.data || (authSession.value?.data as User | undefined);

    if (sessionUser) {
      user.value = sessionUser;
    }

    await nextTick();

    if (!user.value) {
      throw new Error('Unable to load your account.');
    }

    if (user.value.id) {
      $posthog?.identify(String(user.value.id), {
        email: user.value.email,
        name: user.value.full_name,
      });
    }

    $posthog?.capture('user_signed_in', { role: isMerchant.value ? 'merchant' : 'customer' });

    const onboardingRedirect = getPostAuthOnboardingRedirect({
      isCustomer: isCustomer.value,
      isVerified: isVerified.value,
      hasPersonalDetails: Boolean(hasPersonalDetails.value),
      hasCompletedOnboarding: hasCompletedOnboarding.value,
    });

    if (onboardingRedirect) {
      if (onboardingRedirect === 'verify') {
        await execSendVerify('auth/verification/resend', {
          method: 'POST',
          body: { via: 'email' },
        });

        if (!verifyError.value) {
          await router.push({ name: 'verify' });
        }
        return;
      }

      await router.push({ name: 'onboarding' });
      return;
    }

    await fetchCarts();

    const routeRedirect = typeof route.query.redirect === 'string' ? route.query.redirect : null;
    const redirectTarget = getSafeRedirectTarget(options.redirect ?? routeRedirect);

    if (redirectTarget) {
      await router.push(redirectTarget);
    } else if (isMerchant.value) {
      if (!hasStripePayoutCompleted.value) {
        await router.push('/merchant/listing');
      } else {
        await router.push('/merchant/orders');
      }
    } else {
      await router.push({ name: 'marketplace' });
    }
  }

  return {
    completePostAuth,
    fetchingCarts,
    verifyLoading,
  };
}
